import { animate, style, transition, trigger } from '@angular/animations';
import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import {
    atlas,
    CUSTOMERS_REQUEST_CREDIT,
    CUSTOMER_OVERVIEW,
    REPORTS_CREDIT_REQUEST_HISTORY
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    AddCreditRequestResponse,
    CreditRequestStatus,
    GetCreditRequestsResponse,
    RelationshipInformation
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    AddCreditRequestRequest,
    AddCreditRequestService,
    GetCreditRequestsRequest,
    GetCreditRequestsService,
    GetRelationshipRequest,
    GetRelationshipsByClientService,
    GetRelationshipService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    ClassNames,
    DateFormat,
    forLifeOf,
    getDynamicRouteLink,
    LifecycleAware,
    LsqSearchComponent,
    StatusColor,
    SubscriptionLike
} from '@lsq-frontend/common-ui';
import {
    getRouterLinkUrlById,
    Icons,
    ResponsiveService,
    WindowReferenceService
} from '@lsq-frontend/common-ui';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilKeyChanged } from 'rxjs/operators';
import { AnalyticsValue } from '../../constants/analytics.constants';
import { EnumDisplay } from '../../constants/enum-display.constants';
import { DashboardValidators } from '../../validators/dashboard-validators';

@LifecycleAware()
@Component({
    selector: 'increase-credit-limit',
    animations: [
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('200ms ease-in', style({ opacity: 1 }))
            ])
        ])
    ],
    templateUrl: './increase-credit-limit.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./increase-credit-limit.component.scss']
})
export class IncreaseCreditLimitComponent implements OnDestroy, OnInit {
    @ViewChild('customerSearch') public customerSearch: LsqSearchComponent;
    @ViewChild('stickyCardRef') public stickyCardRef: ElementRef;
    @ViewChild('tableRef') public tableRef: ElementRef;
    public title = this._i18n({
        value: 'Increase Credit Limit',
        meaning: 'Increase Credit Limit report title'
    });
    public analyticsName = AnalyticsValue.IncreaseCreditLimitCreditRequestHistory;
    public formGroup: FormGroup;
    public creditRequestHistoryReportLink = getRouterLinkUrlById(
        atlas,
        REPORTS_CREDIT_REQUEST_HISTORY
    );
    public requestNewCustomerCreditLink = getRouterLinkUrlById(atlas, CUSTOMERS_REQUEST_CREDIT);
    public moreHistoryResults = false;
    public selectedCustomerKey: number = null;
    public increaseCreditLimitStatus = CreditRequestStatus;
    public lastIncreaseCustomerName = '';
    public customerInContext: number;
    public historyRequestSubject = new BehaviorSubject<GetCreditRequestsRequest>(undefined);
    public getCreditRequestsRequestStream = this.historyRequestSubject.asObservable();

    private _showCustomerColumnSubject = new BehaviorSubject<boolean>(true);
    public showCustomerColumn$ = this._showCustomerColumnSubject.asObservable();

    public isMobile = false;
    public useStickyCard = true;
    public showAddNewCustomer = false;
    public isStickyCardTallestItem = false;

    // Constants.
    public creditRequestStatus = CreditRequestStatus;
    public enumDisplay = EnumDisplay;
    public dateFormat = DateFormat;
    public classNames = ClassNames;
    public statusColor = StatusColor;
    public icons = Icons;

    // These control the state of the component.
    public selectedCustomer: RelationshipInformation;
    public waitingOnIncreaseRequest = false;
    public increaseResponse: AddCreditRequestResponse;

    // Subscriptions.
    private _windowScrollSubscription: SubscriptionLike;

    public dataSource: LsqApiServiceListSource<GetCreditRequestsResponse, GetCreditRequestsRequest>;

    public dateHeader = this._i18n({
        value: 'Date',
        meaning: 'Table column header'
    });
    public debtorNameHeader = this._i18n({
        value: 'Customer',
        meaning: 'Table column header'
    });
    public requestedAmountHeader = this._i18n({
        value: 'Amount<br/>Requested',
        meaning: 'Table column header'
    });
    public requestedAmountMobileHeader = this._i18n({
        value: 'Amount Requested',
        meaning: 'Table column header'
    });
    public approvedAmountHeader = this._i18n({
        value: 'Amount<br/>Approved',
        meaning: 'Table column header'
    });
    public approvedAmountMobileHeader = this._i18n({
        value: 'Amount Approved',
        meaning: 'Table column header'
    });
    public statusHeader = this._i18n({
        value: 'Status',
        meaning: 'Table column header'
    });
    public reasonHeader = this._i18n({
        value: 'Reason',
        meaning: 'Table column header'
    });

    constructor(
        private _i18n: I18n,
        public getCreditRequestsService: GetCreditRequestsService,
        public getRelationshipService: GetRelationshipService,
        public increaseCreditLimitService: AddCreditRequestService,
        public getRelationshipsByClientService: GetRelationshipsByClientService,
        public userService: UserService,
        public responsiveService: ResponsiveService,
        private windowReferenceService: WindowReferenceService,
        private _activatedRoute: ActivatedRoute
    ) {}

    public ngOnInit(): void {
        this.getCreditRequestsService.setRequestStream(this.getCreditRequestsRequestStream);

        this.getCreditRequestsService.requestStream.pipe(forLifeOf(this)).subscribe((request) => {
            this.moreHistoryResults = false;
        });

        this.getCreditRequestsService.dataStream.pipe(forLifeOf(this)).subscribe((response) => {
            this.moreHistoryResults = response.take < response.totalCount;
        });

        this.formGroup = new FormGroup({
            increaseCreditLimitControl: new FormControl('', [
                Validators.required,
                LsqValidators.currency
            ]),
            customerSearchControl: new FormControl(null, [Validators.required])
        });

        const initialRequest = new GetCreditRequestsRequest({
            debtorKey: this.selectedCustomerKey
        });

        this.dataSource = new LsqApiServiceListSource(this.getCreditRequestsService);

        this._activatedRoute.queryParams.pipe(forLifeOf(this)).subscribe((queryParams) => {
            if (queryParams.key) {
                this.customerInContext = queryParams.key;
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this.retrieveCustomer(this.customerInContext);
            } else {
                this.dataSource.dispatchRequest(initialRequest);
            }
        });

        this.formGroup.valueChanges
            .pipe(forLifeOf(this), distinctUntilKeyChanged('customerSearchControl'))
            .subscribe((value) => {
                if (value.customerSearchControl) {
                    if (
                        !this.selectedCustomer ||
                        (this.selectedCustomer &&
                            this.selectedCustomer.debtorKey !== value.customerSearchControl)
                    ) {
                        // tslint:disable-next-line: no-floating-promises
                        this.retrieveCustomer(value.customerSearchControl);
                    }
                } else {
                    if (this.selectedCustomer) {
                        // tslint:disable-next-line: no-floating-promises
                        this.retrieveCustomer(null);
                    }
                }
            });

        this.responsiveService.smBelow.pipe(forLifeOf(this)).subscribe((isMobile) => {
            this.isMobile = isMobile;
        });

        this.responsiveService.xl.pipe(forLifeOf(this)).subscribe((useStickyCard) => {
            this.useStickyCard = useStickyCard;
        });

        this._windowScrollSubscription = this.windowReferenceService
            .createScrollStream(true)
            .subscribe(() => {
                this.isStickyCardTallestItem =
                    this.stickyCardRef.nativeElement.offsetHeight >=
                    this.tableRef.nativeElement.offsetHeight;
            });
    }

    public ngOnDestroy(): void {
        if (this._windowScrollSubscription) {
            this._windowScrollSubscription.unsubscribe();
        }
    }

    public async retrieveCustomer(debtorKey: number): Promise<void> {
        if (!debtorKey) {
            this._showCustomerColumnSubject.next(true);
            this.selectedCustomer = null;
            this.dataSource.dispatchRequest({ debtorKey: null });
            return;
        } else {
            const newRequest: Partial<GetRelationshipRequest> = {};
            this._showCustomerColumnSubject.next(false);
            newRequest.debtorKey = debtorKey;

            const response = await this.getRelationshipService.executeOnce({
                ...(newRequest as GetRelationshipRequest)
            });
            this.increaseResponse = null;
            this.selectedCustomer = response.relationshipInformation;
            newRequest.debtorKey = this.selectedCustomer.debtorKey;
            this.formGroup.controls.increaseCreditLimitControl.setValidators([
                Validators.required,
                LsqValidators.currency,
                DashboardValidators.creditLimitMinValidator(this.selectedCustomer.creditLimit)
            ]);
            if (this.customerInContext) {
                this.customerSearch.selectItem(response.relationshipInformation);
            }
            this.formGroup.controls.increaseCreditLimitControl.updateValueAndValidity();
            this.dataSource.dispatchRequest(newRequest);
        }
    }

    public setShowAddNewCustomer(value: boolean): void {
        this.showAddNewCustomer = value;
    }

    public async increaseCreditLimit(): Promise<void> {
        if (
            !this.formGroup.value ||
            !this.formGroup.value.customerSearchControl ||
            !this.formGroup.value.increaseCreditLimitControl
        ) {
            return;
        }

        const request = new AddCreditRequestRequest();
        request.debtorKey = this.formGroup.value.customerSearchControl;
        request.requestedAmount = this.formGroup.value.increaseCreditLimitControl;
        this.lastIncreaseCustomerName = this.selectedCustomer.debtorName;
        this.waitingOnIncreaseRequest = true;

        const response = await this.increaseCreditLimitService.executeOnce({ ...request });
        this.waitingOnIncreaseRequest = false;
        this.increaseResponse = response;
        this.customerSearch.clearSearch();
        this.formGroup.reset({
            increaseCreditLimitControl: '',
            customerSearchControl: null
        });
    }

    public getCustomerRoute(debtorKey: string): string {
        return getDynamicRouteLink(getRouterLinkUrlById(atlas, CUSTOMER_OVERVIEW), { debtorKey });
    }
}
